import { Title, Text, Avatar, Button, Popover } from 'rizzui';
import cn from '@core/utils/class-names';
import { useSession } from '@core/providers/session';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

export default function ProfileMenu({
  buttonClassName,
  avatarClassName,
  username = false,
}: {
  buttonClassName?: string;
  avatarClassName?: string;
  username?: boolean;
}) {
  const { user } = useSession();
  return (
    <ProfileMenuPopover>
      <Popover.Trigger>
        <button
          aria-label="Account menu"
          className={cn(
            'w-9 shrink-0 rounded-full outline-none focus-visible:ring-[1.5px] focus-visible:ring-gray-400 focus-visible:ring-offset-2 active:translate-y-px sm:w-10',
            buttonClassName
          )}
        >
          {/* The Marketplace admin portal's avatar: the initial on the brand colour (2026-09-28,
              non-mechanical edit — `color` replaces rizzui's per-name random tint). */}
          <Avatar
            src={user?.image}
            name={user?.name ?? ""}
            color="primary"
            className={cn('!h-9 w-9 font-semibold', avatarClassName)}
          />
          {!!username && (
            <span className="username hidden text-gray-200 dark:text-gray-700 md:inline-flex">
              {user?.name ?? ""}
            </span>
          )}
        </button>
      </Popover.Trigger>

      <Popover.Content className="z-[9999] p-0 dark:bg-gray-100 [&>svg]:dark:fill-gray-100">
        {({ setOpen }) => <DropdownMenu close={() => setOpen(false)} />}
      </Popover.Content>
    </ProfileMenuPopover>
  );
}

function ProfileMenuPopover({ children }: React.PropsWithChildren<{}>) {
  const pathname = useLocation().pathname;
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  return (
    <Popover
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      shadow="sm"
      placement="bottom-end"
    >
      {children}
    </Popover>
  );
}

// Data, not markup: the template's three demo links replaced by this app's real ones.
const menuItems = [
  {
    name: 'Account settings',
    href: '/account',
  },
  {
    name: 'Audit trail',
    href: '/audit',
  },
];

// Non-mechanical edit: `close` shuts the menu on an item click. The pathname effect above only closed
// it on a route change, so "Account settings" chosen on /account left it open.
function DropdownMenu({ close }: { close: () => void }) {
  const { user, signOut, mayReadAudit } = useSession();
  return (
    <div className="w-64 text-left rtl:text-right">
      <div className="flex items-center border-b border-gray-300 px-6 pb-5 pt-6">
        <Avatar src={user?.image} name={user?.name ?? ""} />
        <div className="ms-3">
          <Title as="h6" className="font-semibold">
            {user?.name ?? ""}
          </Title>
          <Text className="text-gray-600">{user?.email ?? ""}</Text>
          {/* Two roles, both shown, because GAP-5 lets the account's role and the role the local
              matrix resolved to disagree. Carried over from the shell this replaced. */}
          {user?.role ? (
            <Text className="mt-1 text-[0.6875rem] capitalize text-gray-500">
              {user.role.replaceAll('_', ' ')}
              {user.matrixRole ? ` · matrix: ${user.matrixRole.replaceAll('_', ' ')}` : ''}
            </Text>
          ) : null}
        </div>
      </div>
      <div className="grid px-3.5 py-3.5 font-medium text-gray-700">
        {/* Non-mechanical edit: /audit is permission-gated, so it is only offered to who may open it. */}
        {menuItems.filter((item) => item.href !== '/audit' || mayReadAudit).map((item) => (
          <Link
            key={item.name}
            to={item.href}
            onClick={close}
            className="group my-0.5 flex items-center rounded-md px-2.5 py-2 hover:bg-gray-100 focus:outline-none hover:dark:bg-gray-50/50"
          >
            {item.name}
          </Link>
        ))}
      </div>
      <div className="border-t border-gray-300 px-6 pb-6 pt-5">
        <Button
          className="h-auto w-full justify-start p-0 font-medium text-gray-700 outline-none focus-within:text-gray-600 hover:text-gray-900 focus-visible:ring-0"
          variant="text"
          onClick={() => void signOut()}
        >
          Sign Out
        </Button>
      </div>
    </div>
  );
}
